import { useEffect, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { api, errMsg } from "../lib/api";
import { SMILEYS } from "../lib/constants";

export const ResolveDialog = ({ item, onClose, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(""); }, [item]);

  const save = async (e) => {
    e.preventDefault();
    if (!text.trim()) return toast.error("Add remarks on how this was resolved");
    setBusy(true);
    try {
      await api.patch(`/feedback/${item.id}`, { status: "Resolved", resolution_remarks: text });
      toast.success(`Resolved ${item.guest_name}'s feedback`);
      onDone();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white rounded-3xl" data-testid="resolve-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-emerald-950">Resolve feedback</DialogTitle>
          {item && <DialogDescription>{SMILEYS[item.rating - 1].emoji} {item.rating}★ · {item.guest_name} · {item.hotel}</DialogDescription>}
        </DialogHeader>
        {item?.remarks && <div className="rounded-xl bg-slate-50 p-3 text-sm text-slate-600">“{item.remarks}”</div>}
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <label className="field-label">Resolution remarks *</label>
            <Textarea data-testid="resolve-remarks-input" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="What was done for the guest? e.g. Apologised, waived minibar charge, offered complimentary dessert" className="rounded-xl" />
          </div>
          <button data-testid="resolve-submit-button" disabled={busy} className="w-full h-11 rounded-full bg-emerald-900 text-amber-50 font-semibold hover:bg-emerald-800 flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Mark as Resolved
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const StatusControl = ({ item, onResolve, onReopen, testId }) => {
  if (item.status === "Pending") {
    return (
      <button data-testid={`${testId}-resolve`} onClick={() => onResolve(item)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-emerald-900 hover:text-amber-50 hover:border-emerald-900 transition-colors">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Pending · Resolve
      </button>
    );
  }
  return (
    <div className="space-y-1">
      <span data-testid={`${testId}-resolved`} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Resolved</span>
      {onReopen && item.rating < 3 && <button data-testid={`${testId}-reopen`} onClick={() => onReopen(item.id)} className="block text-[11px] font-semibold text-slate-500 hover:text-red-600 hover:underline">Reopen</button>}
    </div>
  );
};
